"use client";

import { ArrowDown } from "lucide-react";
import { usePathname } from "next/navigation";
import React from "react";
import { useScrollToSection } from "@/components/hooks/use-scroll-to-section";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MotionLink } from "@/components/ui/Motion";
import SectionBase from "@/components/ui/sections/section-base";
import {
  TypographyH2,
  TypographyH3,
  TypographySmall,
} from "@/components/ui/typography";
import { cls, getServicePath } from "@/lib/utils";
import { SERVICES_SECTION_ID } from "../Services/consts";
import {
  itemElementMotionProperties,
  wrapperElementMotionProperties,
} from "./animations";
import FEATURES_AND_BENEFITS_DATA, {
  CTA_BUTTON_TEXT,
  FEATURES_AND_BENEFITS_SECTION_ID,
  FEATURES_AND_BENEFITS_SUB_TITLE,
} from "./consts";
import FeaturesItem from "./features-item";

const HREF = `#SECTION-${SERVICES_SECTION_ID}`;

function FeaturesAndBenefitsSection() {
  const pathname = usePathname();
  const servicePath = getServicePath(pathname);
  const handleOnClick = useScrollToSection(HREF);

  if (servicePath === null) {
    return null;
  }

  const { description, subtitle, features } =
    FEATURES_AND_BENEFITS_DATA[servicePath];

  return (
    <SectionBase
      id={FEATURES_AND_BENEFITS_SECTION_ID}
      {...wrapperElementMotionProperties}
    >
      <Card
        id="key-elements"
        className={cls`
          bg-secondary-foreground
          grid
          grid-rows-[auto_minmax(0,1fr)]
          md:grid-cols-2
          p-5
          md:p-10
          gap-5
          md:gap-10
        `}
      >
        <CardHeader className={cls`w-full h-min p-0 gap-2 md:sticky md:top-5`}>
          <TypographyH3 className={cls`font-bold text-primary`}>
            {FEATURES_AND_BENEFITS_SUB_TITLE}
          </TypographyH3>
          <TypographyH2 className={cls`border-b-0`}>{subtitle}</TypographyH2>
          <TypographySmall className={cls`mt-0!`}>
            {description}
          </TypographySmall>

          <Button
            className={cls`w-full md:w-fit`}
            variant="secondary"
            onClick={handleOnClick}
            asChild
          >
            <MotionLink href={HREF}>
              <ArrowDown className={cls`mr-1`} />
              {CTA_BUTTON_TEXT}
            </MotionLink>
          </Button>
        </CardHeader>

        <CardContent className={cls`flex flex-1 flex-col p-0 gap-4`}>
          {features.map(({ title, icon, content }) => (
            <FeaturesItem
              key={title}
              title={title}
              icon={icon}
              content={content}
              {...itemElementMotionProperties}
            />
          ))}
        </CardContent>
      </Card>
    </SectionBase>
  );
}

export default FeaturesAndBenefitsSection;
